import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Upload, 
  Download,
  Trash2, 
  Edit2, 
  Search,
  Building2,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Lotes = () => {
  const { selectedPark, parks, createPark, fetchParks } = usePark();
  const [lots, setLots] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLotModal, setShowLotModal] = useState(false);
  const [showParkModal, setShowParkModal] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [indivisoCheck, setIndivisoCheck] = useState(null);
  const fileInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, lotId: null, lotNumber: '' });

  const [lotForm, setLotForm] = useState({
    numero_lote: '',
    metros_cuadrados: '',
    indiviso: '',
    propietario_id: ''
  });

  const [parkForm, setParkForm] = useState({
    nombre: '',
    ubicacion: '',
    moneda: 'MXN'
  });

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [selectedPark]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lotsRes, personsRes, indivisoRes] = await Promise.all([
        axios.get(`${API}/lots?park_id=${selectedPark.id}`),
        axios.get(`${API}/persons?park_id=${selectedPark.id}`),
        axios.get(`${API}/lots/park/${selectedPark.id}/indiviso-check`)
      ]);
      setLots(lotsRes.data);
      setPersons(personsRes.data);
      setIndivisoCheck(indivisoRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePark = async (e) => {
    e.preventDefault();
    try {
      await createPark(parkForm);
      toast.success('Parque creado exitosamente');
      setShowParkModal(false);
      setParkForm({ nombre: '', ubicacion: '', moneda: 'MXN' });
    } catch (error) {
      toast.error('Error al crear el parque');
    }
  };

  const handleSaveLot = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...lotForm,
        metros_cuadrados: parseFloat(lotForm.metros_cuadrados) || 0,
        indiviso: parseFloat(lotForm.indiviso) || 0,
        propietario_id: lotForm.propietario_id || null,
        park_id: selectedPark.id
      };

      if (editingLot) {
        await axios.put(`${API}/lots/${editingLot.id}`, data);
        toast.success('Lote actualizado');
      } else {
        await axios.post(`${API}/lots`, data);
        toast.success('Lote creado');
      }
      
      setShowLotModal(false);
      resetLotForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el lote');
    }
  };

  const handleDeleteLot = async (lotId) => {
    try {
      await axios.delete(`${API}/lots/${lotId}`);
      toast.success('Lote eliminado');
      setDeleteConfirm({ open: false, lotId: null, lotNumber: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el lote');
    }
  };

  const handleEditLot = (lot) => {
    setEditingLot(lot);
    setLotForm({
      numero_lote: lot.numero_lote,
      metros_cuadrados: lot.metros_cuadrados.toString(),
      indiviso: lot.indiviso.toString(),
      propietario_id: lot.propietario_id || ''
    });
    setShowLotModal(true);
  };

  const resetLotForm = () => {
    setEditingLot(null);
    setLotForm({
      numero_lote: '',
      metros_cuadrados: '',
      indiviso: '',
      propietario_id: ''
    });
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/lots/import-csv?park_id=${selectedPark.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Se importaron ${response.data.imported} lotes`);
      if (response.data.errors.length > 0) {
        // Show only first 5 errors to avoid spam
        response.data.errors.slice(0, 5).forEach(err => toast.error(err));
        if (response.data.errors.length > 5) {
          toast.error(`...y ${response.data.errors.length - 5} errores más`);
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    }
    
    e.target.value = '';
  };

  const downloadCSVTemplate = () => {
    const template = `numero_lote,metros_cuadrados,indiviso,propietario_nombre,propietario_email,propietario_telefono
L-01,500,5.5,Juan Pérez,juan@empresa.com,555-111-2222
L-02,750,7.5,María García,maria@empresa.com,555-333-4444
L-03,"1,234.56",10.25,Carlos López,carlos@empresa.com,555-555-6666`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_lotes.csv';
    a.click();
  };

  const filteredLots = lots.filter(lot =>
    lot.numero_lote.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lot.propietario_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // No park selected - show create park
  if (!selectedPark && parks.length === 0) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Directorio de Lotes</h1>
          <p>Configura tu parque industrial para comenzar</p>
        </div>

        <div className="empty-state py-24">
          <div className="empty-state-icon bg-amber-100">
            <Building2 size={32} className="text-amber-600" />
          </div>
          <h3>Crea tu primer parque industrial</h3>
          <p className="mb-6">Necesitas crear un parque antes de agregar lotes.</p>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => setShowParkModal(true)}
            data-testid="create-first-park-btn"
          >
            <Plus size={16} className="mr-2" />
            Crear Parque
          </Button>
        </div>

        {/* Park Modal */}
        <Dialog open={showParkModal} onOpenChange={setShowParkModal}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold">Crear Parque Industrial</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePark}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="label-overline">Nombre del Parque</Label>
                  <Input
                    value={parkForm.nombre}
                    onChange={(e) => setParkForm({ ...parkForm, nombre: e.target.value })}
                    placeholder="Ej: Parque Industrial El Salto"
                    required
                    className="input-industrial"
                    data-testid="park-nombre-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Ubicación</Label>
                  <Input
                    value={parkForm.ubicacion}
                    onChange={(e) => setParkForm({ ...parkForm, ubicacion: e.target.value })}
                    placeholder="Ej: El Salto, Jalisco"
                    required
                    className="input-industrial"
                    data-testid="park-ubicacion-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowParkModal(false)} className="rounded-none">
                  Cancelar
                </Button>
                <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-park-btn">
                  Crear Parque
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="lotes-title">Directorio de Lotes</h1>
        <p>{selectedPark?.nombre} - {lots.length} lotes registrados</p>
      </div>

      {/* Indiviso Alert */}
      {indivisoCheck && (
        <div className={`flex items-center gap-3 p-4 mb-6 border ${indivisoCheck.is_valid ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          {indivisoCheck.is_valid ? (
            <CheckCircle size={20} className="text-emerald-600" />
          ) : (
            <AlertCircle size={20} className="text-amber-600" />
          )}
          <div className="flex-1">
            <p className={`font-medium ${indivisoCheck.is_valid ? 'text-emerald-800' : 'text-amber-800'}`}>
              Suma de indivisos: {indivisoCheck.total_indiviso.toFixed(2)}%
            </p>
            {!indivisoCheck.is_valid && (
              <p className="text-sm text-amber-600">
                Diferencia: {indivisoCheck.difference.toFixed(2)}% (debe ser 100%)
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="action-bar">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar lotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64 input-industrial"
              data-testid="search-lotes"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={downloadCSVTemplate}
            data-testid="download-template-btn"
          >
            <Download size={16} className="mr-2" />
            Plantilla CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            className="hidden"
          />
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-csv-btn"
          >
            <Upload size={16} className="mr-2" />
            Importar CSV
          </Button>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => { resetLotForm(); setShowLotModal(true); }}
            data-testid="add-lot-btn"
          >
            <Plus size={16} className="mr-2" />
            Nuevo Lote
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card-industrial animate-pulse">
          <div className="h-64 bg-slate-100"></div>
        </div>
      ) : filteredLots.length === 0 ? (
        <div className="empty-state py-16 card-industrial">
          <div className="empty-state-icon">
            <Building2 size={24} className="text-slate-400" />
          </div>
          <h3>No hay lotes registrados</h3>
          <p>Comienza agregando lotes manualmente o importando un archivo CSV.</p>
        </div>
      ) : (
        <div className="card-industrial overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="table-blueprint" data-testid="lotes-table">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th className="text-right">m²</th>
                  <th className="text-right">Indiviso %</th>
                  <th>Propietario</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLots.map((lot, index) => (
                  <tr key={lot.id} className="animate-slideIn" style={{ animationDelay: `${index * 30}ms` }}>
                    <td className="font-mono font-medium">{lot.numero_lote}</td>
                    <td className="text-right tabular-nums">{lot.metros_cuadrados.toLocaleString()}</td>
                    <td className="text-right tabular-nums">{lot.indiviso.toFixed(4)}%</td>
                    <td>{lot.propietario_nombre || '-'}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditLot(lot)}
                          className="p-2 hover:bg-slate-100 transition-colors"
                          data-testid={`edit-lot-${lot.numero_lote}`}
                        >
                          <Edit2 size={16} className="text-slate-500" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ open: true, lotId: lot.id, lotNumber: lot.numero_lote })}
                          className="p-2 hover:bg-red-50 transition-colors"
                          data-testid={`delete-lot-${lot.numero_lote}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-medium">
                  <td>Total</td>
                  <td className="text-right tabular-nums">{lots.reduce((sum, l) => sum + l.metros_cuadrados, 0).toLocaleString()}</td>
                  <td className="text-right tabular-nums">{lots.reduce((sum, l) => sum + l.indiviso, 0).toFixed(2)}%</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Lot Modal */}
      <Dialog open={showLotModal} onOpenChange={setShowLotModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingLot ? 'Editar Lote' : 'Nuevo Lote'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLot}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Número de Lote</Label>
                  <Input
                    value={lotForm.numero_lote}
                    onChange={(e) => setLotForm({ ...lotForm, numero_lote: e.target.value })}
                    placeholder="Ej: L-01"
                    required
                    className="input-industrial"
                    data-testid="lot-numero-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Metros Cuadrados</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={lotForm.metros_cuadrados}
                    onChange={(e) => setLotForm({ ...lotForm, metros_cuadrados: e.target.value })}
                    placeholder="500"
                    required
                    className="input-industrial"
                    data-testid="lot-metros-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Indiviso (%)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={lotForm.indiviso}
                  onChange={(e) => setLotForm({ ...lotForm, indiviso: e.target.value })}
                  placeholder="5.5000"
                  required
                  className="input-industrial"
                  data-testid="lot-indiviso-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Propietario</Label>
                <Select
                  value={lotForm.propietario_id || "none"}
                  onValueChange={(value) => setLotForm({ ...lotForm, propietario_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-industrial" data-testid="lot-propietario-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {persons.filter(p => p.tipo === 'propietario').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLotModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-lot-btn">
                {editingLot ? 'Guardar Cambios' : 'Crear Lote'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Park Modal */}
      <Dialog open={showParkModal} onOpenChange={setShowParkModal}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Crear Parque Industrial</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePark}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Nombre del Parque</Label>
                <Input
                  value={parkForm.nombre}
                  onChange={(e) => setParkForm({ ...parkForm, nombre: e.target.value })}
                  placeholder="Ej: Parque Industrial El Salto"
                  required
                  className="input-industrial"
                  data-testid="park-nombre-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Ubicación</Label>
                <Input
                  value={parkForm.ubicacion}
                  onChange={(e) => setParkForm({ ...parkForm, ubicacion: e.target.value })}
                  placeholder="Ej: El Salto, Jalisco"
                  required
                  className="input-industrial"
                  data-testid="park-ubicacion-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowParkModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-park-btn">
                Crear Parque
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
        onConfirm={() => handleDeleteLot(deleteConfirm.lotId)}
        title="¿Estás seguro que deseas eliminar?"
        description={`El lote "${deleteConfirm.lotNumber}" será eliminado permanentemente.`}
        confirmText="Eliminar"
      />
    </div>
  );
};

export default Lotes;
